/**
 * Partición de listas de ids para `.in()` de PostgREST.
 *
 * PostgREST limita la longitud de la URL: un `.in()` con cientos de ids genera una
 * query string de decenas de KB y el servidor responde 400 (ORR-171). Partimos los
 * ids en lotes y combinamos los resultados — cada lote es una request corta.
 *
 * Vivía dentro de `movimientosService.ts`; se extrajo al necesitarlo también en
 * `tareasService` (ORR-259).
 */

export const IN_CHUNK_SIZE = 100

export const splitIntoBatches = <T>(arr: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

export const fetchInChunks = async <R>(
  ids: string[],
  run: (batch: string[]) => Promise<R[]>,
): Promise<R[]> => {
  if (ids.length === 0) return []
  const results = await Promise.all(splitIntoBatches(ids, IN_CHUNK_SIZE).map(run))
  return results.flat()
}

/**
 * Lectura completa de una tabla, página por página.
 *
 * PostgREST corta la respuesta en 1000 filas y NO avisa: no hay error, no hay flag, la lista
 * simplemente llega incompleta. Un `select` pelado sobre una tabla que hoy tiene
 * cientos de filas es una bomba de tiempo silenciosa, así que las lecturas sin `.limit()`
 * propio pasan por acá.
 *
 * `run` recibe los extremos inclusivos para `.range()`. Corta cuando una página vuelve
 * incompleta, que es la única señal de que no hay más.
 */
export const PAGE_SIZE = 1000

export const fetchAllPages = async <R>(
  run: (desde: number, hasta: number) => Promise<R[]>,
): Promise<R[]> => {
  const todas: R[] = []

  for (let desde = 0; ; desde += PAGE_SIZE) {
    const pagina = await run(desde, desde + PAGE_SIZE - 1)
    todas.push(...pagina)
    if (pagina.length < PAGE_SIZE) return todas
  }
}
